import { ComingSoonPin, NotInReportPin, SelectedPin, TrackedPin, UntrackedPin } from '@application/assets';
import { Site, SiteStatus, TrackingState } from '@domain/graphql.types';
import { Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

export type MapPinVariant = 'selected' | 'notInReport' | 'comingSoon' | 'tracked' | 'untracked';

type PinSite = Pick<Site, 'selectedForReport' | 'status' | 'trackingState'>;

/**
 * The one rule deciding the pin of a station, shared by the map markers, the list rows and the station sheet.
 * Selection wins so the open station stays visible whatever its report status; a station beyond the report cap
 * gets the excluded-document pin.
 */
export const getMapPinVariant = (site: PinSite, { isReportCapped, isSelected = false }: { isReportCapped: boolean; isSelected?: boolean }): MapPinVariant => {
  if (isSelected) return 'selected';
  if (isReportCapped && !site.selectedForReport) return 'notInReport';
  if (site.status === SiteStatus.ComingSoon) return 'comingSoon';
  return site.trackingState === TrackingState.Tracked ? 'tracked' : 'untracked';
};

/** Translation key of the status a pin tells, for accessible names and the legend. */
export const mapPinVariantLabelKey: Record<MapPinVariant, string> = {
  selected: 'siteLookup.legend.selected',
  notInReport: 'siteLookup.legend.notInReport',
  comingSoon: 'siteLookup.legend.comingSoon',
  tracked: 'siteLookup.legend.tracked',
  untracked: 'siteLookup.legend.untracked',
};

type Props = {
  height?: number;
  /** the API only returns stations the tenant may access; a false value is still drawn greyed rather than breaking the pin */
  includedInReport?: boolean;
  variant: MapPinVariant;
  width?: number;
};

const MapPin = ({ height, includedInReport = true, variant, width }: Props) => {
  const { t } = useTranslation();
  const {
    palette: { text },
  } = useTheme();

  const fill = includedInReport ? undefined : text.secondary;

  switch (variant) {
    case 'selected':
      return <SelectedPin height={height} width={width} />;
    case 'comingSoon':
      return <ComingSoonPin fill={fill} height={height} width={width} />;
    case 'tracked':
      return <TrackedPin fill={fill} height={height} width={width} />;
    case 'untracked':
      return <UntrackedPin fill={fill} height={height} width={width} />;
    case 'notInReport':
      // A distinct grey excluded-document pin with an accessible name, not only a colour, tells the station matches but is beyond the report cap.
      return (
        <Box
          component="span"
          role="img"
          aria-label={t('siteLookup.legend.notInReport')}
          title={t('siteLookup.legend.notInReport')}
          sx={{ display: 'inline-flex', lineHeight: 0 }}
        >
          <NotInReportPin height={height} width={width} />
        </Box>
      );
  }
};

export default MapPin;
